import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Tabs,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../Constants";
import {
  FEATURE_SWITCH_KEYS,
  buildDefaultFeatureText,
  splitFeatureLines,
} from "./packageFeatureDefaults";

const { TabPane } = Tabs;
const { Title } = Typography;

// SuperAdmin management for the product catalogue: Digital Card plans (the
// Lookups document key = "plans") and the Wedding/Event package catalogue
// (EventProductPackageController). These are two separate backend systems
// sharing one admin page as two tabs.
const CatalogManagementPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("plans");

  const [plans, setPlans] = useState([]);
  const [plansEtag, setPlansEtag] = useState("");
  const [plansLoading, setPlansLoading] = useState(false);
  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm] = Form.useForm();

  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packageModalVisible, setPackageModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm] = Form.useForm();
  const packageHasDigitalPass = Form.useWatch("hasDigitalPassSupport", packageForm);
  const packageProductType = Form.useWatch("productType", packageForm);
  const isWeddingPackage = packageProductType !== "event";
  // Once the SuperAdmin edits either feature textarea (or the package already has saved
  // wording), the capability switches stop regenerating the default lines.
  const featuresTouched = useRef(false);

  // Fills the English/Arabic feature textareas from the currently enabled switches.
  // Mirrors which switches the form shows per product type, and Digital Pass implying QR.
  const syncDefaultFeatures = () => {
    const values = packageForm.getFieldsValue();
    const isWedding = values.productType !== "event";
    const effective = { ...values };
    if (isWedding) {
      effective.hasVipSupport = false;
      effective.hasSpeakerSupport = false;
      effective.hasExhibitorSupport = false;
    } else {
      effective.hasWhatsAppSupport = false;
    }
    if (effective.hasDigitalPassSupport) effective.hasQrCheckIn = true;
    packageForm.setFieldsValue(buildDefaultFeatureText(effective));
  };

  const handlePackageValuesChange = (changed) => {
    if ("featuresEn" in changed || "featuresAr" in changed) {
      featuresTouched.current = true;
    } else if (!featuresTouched.current && FEATURE_SWITCH_KEYS.some((k) => k in changed)) {
      syncDefaultFeatures();
    }
  };

  useEffect(() => {
    if (activeTab === "plans") {
      fetchPlans();
    } else {
      fetchPackages();
    }
  }, [activeTab]);

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/Lookup/plans`);
      setPlans(response.data || []);
      // Save ETag from response headers
      const etagHeader = response.headers["etag"] || response.headers["ETag"] || "";
      setPlansEtag(etagHeader);
    } catch (error) {
      console.error("Error fetching plans:", error);
      message.error(t("catalog.messages.loadPlansFailed"));
    } finally {
      setPlansLoading(false);
    }
  };

  const handlePlanSave = async (values) => {
    setPlansLoading(true);
    try {
      let updatedPlans = [...plans];

      if (editingPlan) {
        // Edit existing plan
        updatedPlans = updatedPlans.map((p) =>
          p.id === editingPlan.id ? { ...p, ...values } : p
        );
      } else {
        // Add new plan
        if (plans.some((p) => p.id === values.id)) {
          message.error(t("catalog.messages.planIdExists"));
          setPlansLoading(false);
          return;
        }
        updatedPlans.push({
          ...values,
          features: values.features ? values.features.split("\n").filter(Boolean) : [],
          featuresAr: values.featuresAr ? values.featuresAr.split("\n").filter(Boolean) : [],
        });
      }

      // Format features if saved as string, and make sure every plan
      // (not just the one being edited) has both arrays populated,
      // since legacy plans in the catalog may be missing featuresAr.
      updatedPlans = updatedPlans.map((p) => {
        const formatted = { ...p };
        formatted.features =
          typeof formatted.features === "string"
            ? formatted.features.split("\n").filter(Boolean)
            : formatted.features || [];
        formatted.featuresAr =
          typeof formatted.featuresAr === "string"
            ? formatted.featuresAr.split("\n").filter(Boolean)
            : formatted.featuresAr || [];
        return formatted;
      });

      // Call PUT with If-Match ETag header
      await axios.put(`${BASE_URL}/Lookup/plans`, updatedPlans, {
        headers: {
          "If-Match": plansEtag,
        },
      });

      message.success(t("catalog.messages.plansUpdated"));
      setPlanModalVisible(false);
      fetchPlans();
    } catch (error) {
      console.error("Error saving plans:", error);
      if (error.response?.status === 409) {
        message.error(t("catalog.messages.conflict"));
        fetchPlans();
      } else {
        message.error(error.response?.data?.message || t("catalog.messages.savePlansFailed"));
      }
    } finally {
      setPlansLoading(false);
    }
  };

  const handlePlanDelete = async (planId) => {
    setPlansLoading(true);
    try {
      const updatedPlans = plans.filter((p) => p.id !== planId);
      await axios.put(`${BASE_URL}/Lookup/plans`, updatedPlans, {
        headers: {
          "If-Match": plansEtag,
        },
      });
      message.success(t("catalog.messages.planDeleted"));
      fetchPlans();
    } catch (error) {
      console.error("Error deleting plan:", error);
      if (error.response?.status === 409) {
        message.error(t("catalog.messages.conflict"));
        fetchPlans();
      } else {
        message.error(t("catalog.messages.deletePlanFailed"));
      }
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchPackages = async () => {
    setPackagesLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/EventProductPackage`);
      setPackages(response.data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
      message.error(t("catalog.messages.loadPackagesFailed"));
    } finally {
      setPackagesLoading(false);
    }
  };

  const handlePackageSave = async (values) => {
    setPackagesLoading(true);
    try {
      const payload = {
        ...values,
        featuresEn: splitFeatureLines(values.featuresEn),
        featuresAr: splitFeatureLines(values.featuresAr),
        id: editingPackage ? editingPackage.id : undefined,
        features: {
          hasWhatsAppSupport: !!values.hasWhatsAppSupport,
          hasSmsSupport: !!values.hasSmsSupport,
          customizationOptions: !!values.customizationOptions,
          // Digital Pass requires QR Check-In (the backend normalizes this too, in
          // EventProductPackageController and again at entitlement-creation time, so
          // this can't actually be saved out of sync — forcing it here as well just
          // keeps what's shown on screen honest with what will be persisted).
          hasQrCheckIn: !!values.hasDigitalPassSupport || !!values.hasQrCheckIn,
          hasDigitalPassSupport: !!values.hasDigitalPassSupport,
          hasLiveDashboard: !!values.hasLiveDashboard,
          hasPostEventAnalytics: !!values.hasPostEventAnalytics,
          hasVipSupport: !!values.hasVipSupport,
          hasSpeakerSupport: !!values.hasSpeakerSupport,
          hasExhibitorSupport: !!values.hasExhibitorSupport,
          hasExportSupport: !!values.hasExportSupport,
        },
      };

      if (editingPackage) {
        await axios.put(`${BASE_URL}/EventProductPackage`, payload);
        message.success(t("catalog.messages.packageUpdated"));
      } else {
        await axios.post(`${BASE_URL}/EventProductPackage`, payload);
        message.success(t("catalog.messages.packageCreated"));
      }

      setPackageModalVisible(false);
      fetchPackages();
    } catch (error) {
      console.error("Error saving package:", error);
      message.error(error.response?.data?.message || error.response?.data || t("catalog.messages.savePackageFailed"));
    } finally {
      setPackagesLoading(false);
    }
  };

  const handleTogglePackageActive = async (record) => {
    setPackagesLoading(true);
    try {
      const updatedPackage = {
        ...record,
        isActive: !record.isActive,
      };
      await axios.put(`${BASE_URL}/EventProductPackage`, updatedPackage);
      message.success(t(updatedPackage.isActive ? "catalog.messages.packageActivated" : "catalog.messages.packageDeactivated"));
      fetchPackages();
    } catch (error) {
      console.error("Error toggling package active state:", error);
      message.error(t("catalog.messages.togglePackageFailed"));
    } finally {
      setPackagesLoading(false);
    }
  };

  const planColumns = [
    { title: t("catalog.columns.planId"), dataIndex: "id", key: "id" },
    { title: t("catalog.columns.planName"), dataIndex: "name", key: "name" },
    {
      title: t("catalog.columns.price"),
      dataIndex: "price",
      key: "price",
      render: (price, record) => `${price} ${record.currency || "SAR"}`,
    },
    { title: t("catalog.columns.durationDays"), dataIndex: "days", key: "days" },
    {
      title: t("catalog.columns.billingPeriod"),
      dataIndex: "period",
      key: "period",
      render: (period) => t(`catalog.period.${period}`, { defaultValue: period }),
    },
    {
      title: t("catalog.columns.status"),
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? t("catalog.status.active") : t("catalog.status.inactive")}
        </Tag>
      ),
    },
    {
      title: t("catalog.columns.actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingPlan(record);
              planForm.setFieldsValue({
                ...record,
                features: record.features?.join("\n") || "",
                featuresAr: record.featuresAr?.join("\n") || "",
              });
              setPlanModalVisible(true);
            }}
          >
            {t("catalog.actions.edit")}
          </Button>
          <Popconfirm
            title={t("catalog.actions.deletePlanConfirm")}
            onConfirm={() => handlePlanDelete(record.id)}
            okText={t("common.yes")}
            cancelText={t("common.no")}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t("catalog.actions.delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const packageColumns = [
    { title: t("catalog.columns.sku"), dataIndex: "sku", key: "sku" },
    { title: t("catalog.columns.nameEn"), dataIndex: "nameEn", key: "nameEn" },
    { title: t("catalog.columns.nameAr"), dataIndex: "nameAr", key: "nameAr" },
    {
      title: t("catalog.columns.type"),
      dataIndex: "productType",
      key: "productType",
      render: (type) => (
        <Tag color={type === "wedding" ? "purple" : "blue"}>{t(`catalog.productType.${type}`, { defaultValue: type }).toUpperCase()}</Tag>
      ),
    },
    {
      title: t("catalog.columns.price"),
      dataIndex: "price",
      key: "price",
      render: (price, record) => `${price} ${record.currency || "SAR"}`,
    },
    { title: t("catalog.columns.maxEvents"), dataIndex: "maxEvents", key: "maxEvents", render: (v) => v ?? 1 },
    {
      title: t("catalog.columns.capacityPerEvent"),
      key: "capacity",
      render: (_, record) =>
        record.productType === "wedding"
          ? t("catalog.capacity.invitations", { count: record.maxInvitations ?? 0 })
          : t("catalog.capacity.registrationsAndVips", {
              registrations: record.maxRegistrations ?? 0,
              vips: record.maxVIPs ?? 0,
            }),
    },
    {
      title: t("catalog.columns.status"),
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleTogglePackageActive(record)}
          checkedChildren={t("catalog.status.active")}
          unCheckedChildren={t("catalog.status.inactive")}
        />
      ),
    },
    {
      title: t("catalog.columns.actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingPackage(record);
              const hasSavedFeatures = !!(record.featuresEn?.length || record.featuresAr?.length);
              // Packages saved before the feature lists existed get the defaults pre-filled
              // from their enabled capabilities; saved wording is shown exactly as stored.
              featuresTouched.current = hasSavedFeatures;
              packageForm.setFieldsValue({
                ...record,
                ...buildDefaultFeatureText(record.features),
                ...(hasSavedFeatures && {
                  featuresEn: record.featuresEn?.join("\n") || "",
                  featuresAr: record.featuresAr?.join("\n") || "",
                }),
                hasWhatsAppSupport: record.features?.hasWhatsAppSupport ?? false,
                hasSmsSupport: record.features?.hasSmsSupport ?? false,
                customizationOptions: record.features?.customizationOptions ?? false,
                hasQrCheckIn: record.features?.hasQrCheckIn ?? false,
                hasDigitalPassSupport: record.features?.hasDigitalPassSupport ?? false,
                hasLiveDashboard: record.features?.hasLiveDashboard ?? false,
                hasPostEventAnalytics: record.features?.hasPostEventAnalytics ?? false,
                hasVipSupport: record.features?.hasVipSupport ?? false,
                hasSpeakerSupport: record.features?.hasSpeakerSupport ?? false,
                hasExhibitorSupport: record.features?.hasExhibitorSupport ?? false,
                hasExportSupport: record.features?.hasExportSupport ?? false,
              });
              setPackageModalVisible(true);
            }}
          >
            {t("catalog.actions.edit")}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px", background: "#f5f7fa", minHeight: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <Title level={2} style={{ margin: 0 }}>{t("catalog.title")}</Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => (activeTab === "plans" ? fetchPlans() : fetchPackages())}
          >
            {t("catalog.refresh")}
          </Button>
          {activeTab === "plans" ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingPlan(null);
                planForm.resetFields();
                setPlanModalVisible(true);
              }}
            >
              {t("catalog.addPlan")}
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingPackage(null);
                featuresTouched.current = false;
                packageForm.resetFields();
                setPackageModalVisible(true);
              }}
            >
              {t("catalog.addPackage")}
            </Button>
          )}
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
        <TabPane tab={t("catalog.tabs.plans")} key="plans">
          <Table
            columns={planColumns}
            dataSource={plans}
            rowKey="id"
            loading={plansLoading}
            pagination={false}
          />
        </TabPane>
        <TabPane tab={t("catalog.tabs.packages")} key="packages">
          <Table
            columns={packageColumns}
            dataSource={packages}
            rowKey="id"
            loading={packagesLoading}
            pagination={false}
          />
        </TabPane>
      </Tabs>

      {/* Plan Add/Edit Modal */}
      <Modal
        title={editingPlan ? t("catalog.modal.editPlan") : t("catalog.modal.addPlan")}
        visible={planModalVisible}
        onCancel={() => setPlanModalVisible(false)}
        onOk={() => planForm.submit()}
        confirmLoading={plansLoading}
        destroyOnClose
      >
        <Form form={planForm} layout="vertical" onFinish={handlePlanSave}>
          <Form.Item
            name="id"
            label={t("catalog.form.planId")}
            rules={[{ required: true, message: t("catalog.form.planIdRequired") }]}
          >
            <Input disabled={!!editingPlan} placeholder="monthly" />
          </Form.Item>
          <Form.Item
            name="name"
            label={t("catalog.form.displayName")}
            rules={[{ required: true, message: t("catalog.form.displayNameRequired") }]}
          >
            <Input placeholder="Individual Monthly" />
          </Form.Item>
          <Form.Item
            name="price"
            label={t("catalog.form.price")}
            rules={[{ required: true, message: t("catalog.form.priceRequired") }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="currency"
            label={t("catalog.form.currency")}
            initialValue="SAR"
            rules={[{ required: true, message: t("catalog.form.currencyRequired") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="days"
            label={t("catalog.form.durationInDays")}
            rules={[{ required: true, message: t("catalog.form.durationRequired") }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="period"
            label={t("catalog.form.billingPeriod")}
            initialValue="Monthly"
            rules={[{ required: true, message: t("catalog.form.billingPeriodRequired") }]}
          >
            <Select>
              <Select.Option value="Monthly">{t("catalog.period.Monthly")}</Select.Option>
              <Select.Option value="Yearly">{t("catalog.period.Yearly")}</Select.Option>
              <Select.Option value="OneTime">{t("catalog.period.OneTime")}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="isActive" label={t("catalog.form.activePlan")} valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item name="features" label={t("catalog.form.featuresEn")}>
            <Input.TextArea rows={4} placeholder={t("catalog.form.featuresEnPlaceholder")} />
          </Form.Item>
          <Form.Item name="featuresAr" label={t("catalog.form.featuresAr")}>
            <Input.TextArea rows={4} dir="rtl" placeholder={t("catalog.form.featuresArPlaceholder")} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Wedding/Event Package Add/Edit Modal */}
      <Modal
        title={editingPackage ? t("catalog.modal.editPackage") : t("catalog.modal.addPackage")}
        visible={packageModalVisible}
        onCancel={() => setPackageModalVisible(false)}
        onOk={() => packageForm.submit()}
        confirmLoading={packagesLoading}
        destroyOnClose
      >
        <Form
          form={packageForm}
          layout="vertical"
          onFinish={handlePackageSave}
          onValuesChange={handlePackageValuesChange}
        >
          <Form.Item
            name="sku"
            label={t("catalog.form.sku")}
            rules={[{ required: true, message: t("catalog.form.skuRequired") }]}
          >
            <Input disabled={!!editingPackage} placeholder={t("catalog.form.skuPlaceholder")} />
          </Form.Item>
          <Form.Item
            name="nameEn"
            label={t("catalog.form.englishName")}
            rules={[{ required: true, message: t("catalog.form.englishNameRequired") }]}
          >
            <Input placeholder={t("catalog.form.englishNamePlaceholder")} />
          </Form.Item>
          <Form.Item
            name="nameAr"
            label={t("catalog.form.arabicName")}
            rules={[{ required: true, message: t("catalog.form.arabicNameRequired") }]}
          >
            <Input placeholder={t("catalog.form.arabicNamePlaceholder")} />
          </Form.Item>
          <Form.Item
            name="productType"
            label={t("catalog.form.productType")}
            initialValue="wedding"
            rules={[{ required: true, message: t("catalog.form.productTypeRequired") }]}
          >
            <Select
              onChange={(value) => {
                if (value === "wedding") {
                  packageForm.setFieldsValue({
                    hasVipSupport: false,
                    hasSpeakerSupport: false,
                    hasExhibitorSupport: false,
                  });
                } else {
                  packageForm.setFieldsValue({ hasWhatsAppSupport: false });
                }
                if (!featuresTouched.current) syncDefaultFeatures();
              }}
            >
              <Select.Option value="wedding">{t("catalog.productType.wedding")}</Select.Option>
              <Select.Option value="event">{t("catalog.productType.event")}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="price"
            label={t("catalog.form.price")}
            rules={[{ required: true, message: t("catalog.form.priceRequired") }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="currency"
            label={t("catalog.form.currency")}
            initialValue="SAR"
            rules={[{ required: true, message: t("catalog.form.currencyRequired") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="maxEvents"
            label={t("catalog.form.maxEvents")}
            initialValue={1}
            tooltip={t("catalog.form.maxEventsTooltip")}
            rules={[{ required: true, message: t("catalog.form.maxEventsRequired") }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          {isWeddingPackage ? (
            <Form.Item
              name="maxInvitations"
              label={t("catalog.form.maxInvitations")}
              rules={[{ required: true, message: t("catalog.form.maxInvitationsRequired") }]}
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="maxRegistrations"
                label={t("catalog.form.maxRegistrations")}
                tooltip={t("catalog.form.maxRegistrationsTooltip")}
                rules={[{ required: true, message: t("catalog.form.maxRegistrationsRequired") }]}
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="maxVIPs"
                label={t("catalog.form.maxVips")}
                tooltip={t("catalog.form.maxVipsTooltip")}
                initialValue={0}
                rules={[{ required: true, message: t("catalog.form.maxVipsRequired") }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </>
          )}
          <Form.Item name="isActive" label={t("catalog.form.activePackage")} valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>

          {isWeddingPackage && (
            <Form.Item name="hasWhatsAppSupport" label={t("catalog.form.whatsAppSupport")} valuePropName="checked" initialValue={false}>
              <Switch />
            </Form.Item>
          )}
          <Form.Item name="customizationOptions" label={t("catalog.form.customizationOptions")} valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item
            name="hasDigitalPassSupport"
            label={t("catalog.form.digitalPasses")}
            valuePropName="checked"
            initialValue={false}
          >
            <Switch
              onChange={(checked) => {
                // Digital Pass requires QR Check-In (the backend enforces/normalizes
                // this too — see EventProductPackageController — this just keeps the
                // form's displayed state honest with what will actually be saved).
                if (checked) {
                  packageForm.setFieldsValue({ hasQrCheckIn: true });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="hasQrCheckIn"
            label={t("catalog.form.qrCheckIn")}
            valuePropName="checked"
            initialValue={false}
            tooltip={packageHasDigitalPass ? t("catalog.form.qrCheckInTooltip") : undefined}
          >
            <Switch disabled={!!packageHasDigitalPass} />
          </Form.Item>
          <Form.Item name="hasLiveDashboard" label={t("catalog.form.liveDashboard")} valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item name="hasPostEventAnalytics" label={t("catalog.form.postEventAnalytics")} valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          {!isWeddingPackage && (
            <>
              <Form.Item name="hasVipSupport" label={t("catalog.form.vipRegistrations")} valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
              <Form.Item name="hasSpeakerSupport" label={t("catalog.form.speakerManagement")} valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
              <Form.Item name="hasExhibitorSupport" label={t("catalog.form.exhibitorManagement")} valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
            </>
          )}
          <Form.Item name="hasExportSupport" label={t("catalog.form.attendeeCsvExport")} valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item
            name="featuresEn"
            label={t("catalog.form.featuresEn")}
            tooltip={t("catalog.form.packageFeaturesTooltip")}
          >
            <Input.TextArea rows={5} placeholder={t("catalog.form.featuresEnPlaceholder")} />
          </Form.Item>
          <Form.Item name="featuresAr" label={t("catalog.form.featuresAr")}>
            <Input.TextArea rows={5} dir="rtl" placeholder={t("catalog.form.featuresArPlaceholder")} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CatalogManagementPage;
